import type { WeatherPacket } from '../lib/weather';
import type { CellHookData } from 'jspdf-autotable';

export interface PacketDayRow {
    date: string;
    modelPrecip: number | null;
    noaaPrecip: number | null;
    high: number | null;
    low: number | null;
    rain: boolean;
}

/**
 * Name the NOAA station the packet's station column came from, with its distance
 * from the site.
 *
 * This is the line that has to survive a builder's rep reading it weeks later. A
 * named station 3.6 miles out corroborates the site; "Charlotte Douglas" printed
 * on a Hickory lot 45 miles away is the thing that loses the EPO, because the
 * metro can be dry on the day the site floods. Null station means the lot's
 * weather predates per-site selection and has not been re-pulled yet, so the line
 * stays generic rather than naming a station we can't stand behind.
 */
export function stationLine(packet: WeatherPacket): string {
    const s = packet.noaa_station;
    if (!s?.id) return 'Station data from NOAA NCEI GHCND.';
    const name = s.name ?? s.id;
    const dist = s.distance_mi != null ? `, ${s.distance_mi} mi from site` : '';
    return `Station data from NOAA NCEI GHCND, ${name} (${s.id})${dist}.`;
}

/** Short form for the packet's metadata block and the on-screen header chip. */
export function stationSummary(packet: WeatherPacket): string | null {
    const s = packet.noaa_station;
    if (!s?.id) return null;
    const name = s.name ?? s.id;
    return s.distance_mi != null ? `${name} (${s.id}), ${s.distance_mi} mi from site` : `${name} (${s.id})`;
}

// Build the dispute packet document. jsPDF and the autotable plugin are
// dynamically imported so they code-split out of the main bundle and only
// load when someone actually generates a packet.
async function buildPacketDoc(packet: WeatherPacket, rows: PacketDayRow[]) {
    const { jsPDF } = await import('jspdf');
    const autoTable = (await import('jspdf-autotable')).default;

    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const left = 40;
    let y = 48;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Precision Performance Concrete', left, y);
    y += 20;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.text('Prep-to-Pour Weather Record', left, y);
    y += 26;

    doc.setFontSize(10);
    const rainDays = rows.filter((r) => r.rain).length;
    // The station belongs up here next to the lot, not buried in 8pt footer text.
    // A builder's rep skims the header; that is where the corroborating station
    // has to be readable.
    const meta: [string, string][] = [
        ['Builder', packet.builder],
        ['Community', packet.community],
        ['Lot', packet.lot_number],
        ['Address', packet.address ?? '—'],
        ['Prep completed', packet.prep_date ?? '—'],
        ['Pour completed', packet.pour_date ?? '—'],
        ['Days with rain', String(rainDays)],
        ['Weather station', stationSummary(packet) ?? '—'],
    ];
    for (const [k, v] of meta) {
        doc.setFont('helvetica', 'bold');
        doc.text(`${k}:`, left, y);
        doc.setFont('helvetica', 'normal');
        doc.text(String(v), left + 120, y);
        y += 16;
    }
    y += 8;

    const rainIdx = new Set<number>();
    const body = rows.map((r, i) => {
        if (r.rain) rainIdx.add(i);
        return [
            r.date,
            r.modelPrecip == null ? '—' : r.modelPrecip.toFixed(2),
            r.noaaPrecip == null ? '—' : r.noaaPrecip.toFixed(2),
            r.high == null ? '—' : String(r.high),
            r.low == null ? '—' : String(r.low),
        ];
    });

    autoTable(doc, {
        startY: y,
        head: [['Date', 'Precip Model (in)', 'Precip NOAA (in)', 'High (F)', 'Low (F)']],
        body,
        styles: { font: 'helvetica', fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [31, 41, 51], textColor: 255 },
        columnStyles: {
            1: { halign: 'right' },
            2: { halign: 'right' },
            3: { halign: 'right' },
            4: { halign: 'right' },
        },
        // Highlight days where either source recorded rain
        didParseCell: (data: CellHookData) => {
            if (data.section === 'body' && rainIdx.has(data.row.index)) {
                data.cell.styles.fillColor = [219, 234, 254] as [number, number, number];
            }
        },
    });

    const finalY = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y;
    let fy = finalY + 26;
    doc.setFontSize(8);
    doc.setTextColor(120);
    // Two lines on purpose. The station name pushes the combined sources line past
    // the printable width on letter, and a clipped citation is worse than none.
    doc.text('Sources: model data by Open-Meteo.com.', left, fy);
    fy += 10;
    doc.text(stationLine(packet), left, fy);
    fy += 12;
    doc.text(
        `Generated ${new Date().toLocaleDateString('en-US')} by Precision Performance Concrete.`,
        left,
        fy
    );

    return doc;
}

function packetFilename(packet: WeatherPacket): string {
    return `weather-${packet.community.replace(/\s+/g, '-')}-lot-${packet.lot_number}.pdf`;
}

export async function downloadPacketPdf(packet: WeatherPacket, rows: PacketDayRow[]) {
    const doc = await buildPacketDoc(packet, rows);
    doc.save(packetFilename(packet));
}

// Returns the packet as a base64 string (no data: prefix) for the CompanyCam
// document upload in part two.
export async function packetPdfBase64(packet: WeatherPacket, rows: PacketDayRow[]): Promise<string> {
    const doc = await buildPacketDoc(packet, rows);
    const uri = doc.output('datauristring');
    return uri.substring(uri.indexOf(',') + 1);
}
